import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import NavBar from "@/components/NavBar";
import AdminUsersPanel from "@/components/admin/AdminUsersPanel";
import AdminSampleVideosPanel from "@/components/admin/AdminSampleVideosPanel";
import {
    Lightning,
    PencilSimple,
    Trash,
    Plus,
    UploadSimple,
    FilePdf,
    CheckCircle,
    Clock,
    X,
    Books,
    Users,
    FilmSlate,
} from "@phosphor-icons/react";

export default function Admin() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [recipes, setRecipes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [tab, setTab] = useState("users"); // 'users' (default — most actionable) | 'content'

    useEffect(() => {
        if (!authLoading) {
            if (!user) navigate("/login");
            else if (!user.is_admin) navigate("/dashboard");
        }
    }, [user, authLoading, navigate]);

    const load = async () => {
        try {
            const r = await api.get("/admin/recipes");
            setRecipes(r.data);
        } catch (err) {
            toast.error("Failed to load recipes");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.is_admin) load();
    }, [user]);

    const onDelete = async (id) => {
        if (!window.confirm("Delete this recipe permanently?")) return;
        try {
            await api.delete(`/admin/recipes/${id}`);
            toast.success("Recipe deleted");
            load();
        } catch (err) {
            toast.error("Delete failed");
        }
    };

    if (authLoading || !user?.is_admin) return null;

    return (
        <div className="min-h-screen bg-background">
            <NavBar variant="dashboard" />
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-10">
                <div className="mb-6">
                    <p className="label-mono text-electric-blue mb-2">ADMIN CONTROL // FOUNDER ONLY</p>
                    <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tighter">
                        Admin <span className="text-electric-blue">Console</span>
                    </h1>
                    <p className="text-muted-foreground mt-2">
                        Manage members, comp accounts, and platform content.
                    </p>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-white/10 mb-6 -mx-1">
                    <TabButton active={tab === "users"} onClick={() => setTab("users")} icon={Users} label="USERS & COMP ACCOUNTS" testId="admin-tab-users" />
                    <TabButton active={tab === "content"} onClick={() => setTab("content")} icon={Books} label="RECIPES & PLAYBOOKS" testId="admin-tab-content" />
                    <TabButton active={tab === "videos"} onClick={() => setTab("videos")} icon={FilmSlate} label="SAMPLE VIDEOS" testId="admin-tab-videos" />
                </div>

                {tab === "users" && <AdminUsersPanel />}

                {tab === "videos" && <AdminSampleVideosPanel />}

                {tab === "content" && (
                    <div>
                        <div className="flex items-center justify-between mb-5">
                            <p className="text-sm text-muted-foreground">
                                Upload PDFs, edit metadata, and release blueprints to the syndicate.
                            </p>
                            <button
                                data-testid="admin-new-recipe-btn"
                                onClick={() => setEditing("new")}
                                className="bg-electric-blue hover:bg-electric-blue-dark text-white font-bold px-5 py-3 text-xs tracking-wider uppercase transition-all hover:shadow-[0_0_20px_rgba(0,123,255,0.5)] flex items-center gap-2"
                            >
                                <Plus size={16} weight="bold" /> New Recipe
                            </button>
                        </div>
                        <div className="bg-[#0A0A0A] border border-white/10">
                    <table className="w-full">
                        <thead className="border-b border-white/10">
                            <tr>
                                <th className="text-left px-5 py-3 label-mono text-[10px]">TITLE</th>
                                <th className="text-left px-5 py-3 label-mono text-[10px]">SECTION</th>
                                <th className="text-left px-5 py-3 label-mono text-[10px]">CATEGORY</th>
                                <th className="text-left px-5 py-3 label-mono text-[10px]">STATUS</th>
                                <th className="text-left px-5 py-3 label-mono text-[10px]">PDF</th>
                                <th className="text-right px-5 py-3 label-mono text-[10px]">ACTIONS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={6} className="p-6 label-mono text-muted-foreground">LOADING...</td></tr>
                            )}
                            {!loading && recipes.length === 0 && (
                                <tr><td colSpan={6} className="p-6 label-mono text-muted-foreground">NO RECIPES YET</td></tr>
                            )}
                            {recipes.map((r, i) => (
                                <tr
                                    key={r.id}
                                    data-testid={`admin-recipe-row-${i}`}
                                    className="border-b border-white/5 hover:bg-electric-blue/5 transition-colors"
                                >
                                    <td className="px-5 py-4 max-w-md">
                                        <p className="font-semibold text-sm truncate">{r.title}</p>
                                        <p className="text-xs text-muted-foreground truncate">{r.description}</p>
                                    </td>
                                    <td className="px-5 py-4">
                                        <span className={`inline-flex px-2 py-1 text-[10px] label-mono border ${
                                            r.section === "playbook"
                                                ? "text-electric-blue border-electric-blue/40 bg-electric-blue/5"
                                                : r.section === "deployment_hub"
                                                ? "text-emerald-300 border-emerald-400/40 bg-emerald-500/5"
                                                : "text-purple-300 border-purple-400/40 bg-purple-500/5"
                                        }`}>
                                            {r.section === "playbook" ? "PLAYBOOK" : r.section === "deployment_hub" ? "HUB" : "RECIPE"}
                                        </span>
                                    </td>
                                    <td className="px-5 py-4 text-xs">{r.category}</td>
                                    <td className="px-5 py-4">
                                        {r.status === "active" ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 text-[10px] label-mono">
                                                <CheckCircle size={12} weight="fill" /> ACTIVE
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-500/10 text-yellow-400 border border-yellow-500/30 text-[10px] label-mono">
                                                <Clock size={12} weight="fill" /> COMING SOON
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-5 py-4">
                                        {r.pdf_file_id ? (
                                            <span className="inline-flex items-center gap-1 text-electric-blue text-xs">
                                                <FilePdf size={14} weight="fill" /> Linked
                                            </span>
                                        ) : (
                                            <span className="text-xs text-muted-foreground">—</span>
                                        )}
                                    </td>
                                    <td className="px-5 py-4">
                                        <div className="flex items-center justify-end gap-2">
                                            <button
                                                onClick={() => setEditing(r)}
                                                data-testid={`admin-edit-${i}`}
                                                className="p-2 text-muted-foreground hover:text-electric-blue transition-colors border border-white/10 hover:border-electric-blue"
                                                aria-label="Edit"
                                            >
                                                <PencilSimple size={14} weight="bold" />
                                            </button>
                                            <button
                                                onClick={() => onDelete(r.id)}
                                                data-testid={`admin-delete-${i}`}
                                                className="p-2 text-muted-foreground hover:text-red-400 transition-colors border border-white/10 hover:border-red-400"
                                                aria-label="Delete"
                                            >
                                                <Trash size={14} weight="bold" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                        </div>
                    </div>
                )}
            </div>

            {editing && (
                <RecipeEditModal
                    recipe={editing === "new" ? null : editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => {
                        setEditing(null);
                        load();
                    }}
                />
            )}
        </div>
    );
}

function TabButton({ active, onClick, icon: Icon, label, testId }) {
    return (
        <button
            data-testid={testId}
            onClick={onClick}
            className={`flex items-center gap-2 px-5 py-3 label-mono text-[11px] transition-all border-b-2 -mb-px ${
                active
                    ? "border-electric-blue text-electric-blue bg-electric-blue/5"
                    : "border-transparent text-muted-foreground hover:text-white"
            }`}
        >
            <Icon size={14} weight={active ? "fill" : "regular"} />
            {label}
        </button>
    );
}

function RecipeEditModal({ recipe, onClose, onSaved }) {
    const isNew = !recipe;
    const [form, setForm] = useState({
        title: recipe?.title || "",
        description: recipe?.description || "",
        category: recipe?.category || "Historical Mystery",
        rpm_estimate: recipe?.rpm_estimate || "",
        cover_url: recipe?.cover_url || "",
        pdf_file_id: recipe?.pdf_file_id || null,
        status: recipe?.status || "active",
        release_label: recipe?.release_label || "",
        section: recipe?.section || "recipe",
        walkthrough_video_url: recipe?.walkthrough_video_url || "",
    });
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.name.toLowerCase().endsWith(".pdf")) {
            toast.error("PDF files only");
            return;
        }
        const fd = new FormData();
        fd.append("file", file);
        setUploading(true);
        try {
            const r = await api.post("/admin/upload-pdf", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setForm((f) => ({ ...f, pdf_file_id: r.data.id }));
            toast.success(`PDF uploaded: ${file.name}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const onSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...form,
                release_label: form.release_label || null,
                walkthrough_video_url: form.walkthrough_video_url || null,
            };
            if (isNew) {
                await api.post("/admin/recipes", payload);
                toast.success("Recipe created");
            } else {
                await api.put(`/admin/recipes/${recipe.id}`, payload);
                toast.success("Recipe updated");
            }
            onSaved();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            data-testid="admin-recipe-modal"
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-2xl bg-[#0A0A0A] border border-electric-blue/40 neon-border my-8 max-h-[90vh] overflow-y-auto scrollbar-thin"
            >
                <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 bg-[#0A0A0A] z-10">
                    <div>
                        <p className="label-mono text-electric-blue">{isNew ? "NEW RECIPE" : "EDIT RECIPE"}</p>
                        <h2 className="font-heading font-bold text-xl">
                            {isNew ? "Create Blueprint" : recipe.title}
                        </h2>
                    </div>
                    <button onClick={onClose} className="text-muted-foreground hover:text-white" aria-label="Close">
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={onSave} className="p-6 space-y-5">
                    <Field label="SECTION">
                        <div className="flex gap-2">
                            {[
                                { value: "recipe", label: "Recipe Vault" },
                                { value: "playbook", label: "Growth Playbook" },
                                { value: "deployment_hub", label: "Deployment Hub" },
                            ].map((opt) => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    data-testid={`admin-form-section-${opt.value}`}
                                    onClick={() => setForm({ ...form, section: opt.value })}
                                    className={`flex-1 px-3 py-2.5 text-[10px] label-mono border transition-all ${
                                        form.section === opt.value
                                            ? "bg-electric-blue border-electric-blue text-white"
                                            : "bg-transparent border-white/10 text-muted-foreground hover:border-electric-blue/50 hover:text-white"
                                    }`}
                                >
                                    {opt.label.toUpperCase()}
                                </button>
                            ))}
                        </div>
                    </Field>

                    <Field label="TITLE">
                        <input
                            data-testid="admin-form-title"
                            type="text"
                            required
                            value={form.title}
                            onChange={(e) => setForm({ ...form, title: e.target.value })}
                            className="w-full bg-[#060606] border border-white/10 px-4 py-2.5 text-white outline-none focus:border-electric-blue focus:ring-1 focus:ring-electric-blue text-sm"
                        />
                    </Field>

                    <Field label="DESCRIPTION">
                        <textarea
                            data-testid="admin-form-description"
                            required
                            rows={3}
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            className="w-full bg-[#060606] border border-white/10 px-4 py-2.5 text-white outline-none focus:border-electric-blue focus:ring-1 focus:ring-electric-blue text-sm"
                        />
                    </Field>

                    <div className="grid grid-cols-2 gap-4">
                        <Field label="CATEGORY">
                            <input
                                data-testid="admin-form-category"
                                type="text"
                                required
                                value={form.category}
                                onChange={(e) => setForm({ ...form, category: e.target.value })}
                                className="w-full bg-[#060606] border border-white/10 px-4 py-2.5 text-white outline-none focus:border-electric-blue focus:ring-1 focus:ring-electric-blue text-sm"
                            />
                        </Field>
                        <Field label="RPM ESTIMATE">
                            <input
                                data-testid="admin-form-rpm"
                                type="text"
                                value={form.rpm_estimate}
                                onChange={(e) => setForm({ ...form, rpm_estimate: e.target.value })}
                                placeholder="$12-25"
                                className="w-full bg-[#060606] border border-white/10 px-4 py-2.5 text-white outline-none focus:border-electric-blue focus:ring-1 focus:ring-electric-blue text-sm"
                            />
                        </Field>
                    </div>

                    <Field label="COVER IMAGE URL">
                        <input
                            data-testid="admin-form-cover"
                            type="url"
                            value={form.cover_url}
                            onChange={(e) => setForm({ ...form, cover_url: e.target.value })}
                            placeholder="https://..."
                            className="w-full bg-[#060606] border border-white/10 px-4 py-2.5 text-white outline-none focus:border-electric-blue focus:ring-1 focus:ring-electric-blue text-sm font-mono"
                        />
                    </Field>

                    <div className="grid grid-cols-2 gap-4">
                        <Field label="STATUS">
                            <select
                                data-testid="admin-form-status"
                                value={form.status}
                                onChange={(e) => setForm({ ...form, status: e.target.value })}
                                className="w-full bg-[#060606] border border-white/10 px-4 py-2.5 text-white outline-none focus:border-electric-blue text-sm"
                            >
                                <option value="active">Active (Downloadable)</option>
                                <option value="coming_soon">Coming Soon</option>
                            </select>
                        </Field>
                        <Field label="RELEASE LABEL (optional)">
                            <input
                                data-testid="admin-form-release"
                                type="text"
                                value={form.release_label}
                                onChange={(e) => setForm({ ...form, release_label: e.target.value })}
                                placeholder="Releasing Week 2"
                                className="w-full bg-[#060606] border border-white/10 px-4 py-2.5 text-white outline-none focus:border-electric-blue text-sm"
                            />
                        </Field>
                    </div>

                    <Field label="HOW-TO WALKTHROUGH VIDEO URL (optional)" hint="YouTube embed, Loom, Vimeo, or direct .mp4. Shows a 'How to build from this' button on the Recipe card.">
                        <input
                            data-testid="admin-form-walkthrough-url"
                            type="url"
                            value={form.walkthrough_video_url}
                            onChange={(e) => setForm({ ...form, walkthrough_video_url: e.target.value })}
                            placeholder="https://www.youtube.com/embed/VIDEO_ID  or  https://www.loom.com/embed/SHARE_ID"
                            className="w-full bg-[#060606] border border-white/10 px-4 py-2.5 text-white outline-none focus:border-electric-blue text-sm"
                        />
                    </Field>

                    <Field label="PDF BLUEPRINT">
                        <div className="flex items-center gap-3">
                            <label
                                data-testid="admin-form-pdf-upload"
                                className="cursor-pointer inline-flex items-center gap-2 bg-electric-blue/10 border border-electric-blue/40 hover:bg-electric-blue/20 px-4 py-2.5 text-xs label-mono text-electric-blue transition-all"
                            >
                                <UploadSimple size={14} weight="bold" />
                                {uploading ? "UPLOADING..." : "UPLOAD PDF"}
                                <input
                                    type="file"
                                    accept="application/pdf,.pdf"
                                    onChange={handleUpload}
                                    disabled={uploading}
                                    className="hidden"
                                />
                            </label>
                            {form.pdf_file_id ? (
                                <span className="inline-flex items-center gap-1 text-emerald-400 text-xs label-mono">
                                    <FilePdf size={14} weight="fill" /> LINKED
                                </span>
                            ) : (
                                <span className="text-xs text-muted-foreground italic">No PDF attached</span>
                            )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                            Max 25MB. Replaces any previously linked PDF on save.
                        </p>
                    </Field>

                    <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-5 py-2.5 text-xs label-mono border border-white/10 hover:border-white/30 transition-colors"
                        >
                            CANCEL
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            data-testid="admin-form-save"
                            className="bg-electric-blue hover:bg-electric-blue-dark disabled:opacity-50 text-white font-bold px-6 py-2.5 text-xs tracking-wider uppercase transition-all hover:shadow-[0_0_20px_rgba(0,123,255,0.5)]"
                        >
                            {saving ? "SAVING..." : isNew ? "CREATE RECIPE" : "SAVE CHANGES"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function Field({ label, hint, children }) {
    return (
        <div>
            <label className="label-mono mb-2 block">
                {label}
                {hint && <span className="ml-2 text-[9px] text-white/40 normal-case tracking-normal">— {hint}</span>}
            </label>
            {children}
        </div>
    );
}
